import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { OAuthStorageService } from '@qaroni-app/auth/services/o-auth-storage.service';
import { QaroniIntermediate } from '@qaroni-core/types/qaroni-intermediate/qaroni-intermediate';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { Workplace, WorkplaceDTO } from '../types/workplace';
import { WorkplaceAddressDTO } from '../types/workplace-address';
import { WorkplaceCache } from '../types/workplace-cache.class';
import { WorkplaceApiObject } from '../types/workplace-response';
import { WorkplacesCache } from '../types/workplaces-cache.class';
import { WorkplaceAddressHttpService } from './workplace-address-http.service';
import { WorkplaceHttpService } from './workplace-http.service';
import { WorkplacePolicyService } from './workplace-policy.service';
import { WorkplaceSnackbarsService } from './workplace-snackbars.service';

@Injectable({
  providedIn: 'root',
})
export class WorkplaceService extends QaroniIntermediate {
  protected readonly listSubject = new BehaviorSubject<Workplace[]>(null);
  private workplaces: WorkplacesCache = new WorkplacesCache();

  protected readonly getSubject = new Subject<Workplace>();
  private workplace: WorkplaceCache = new WorkplaceCache();

  private http = inject(WorkplaceHttpService);
  private addressHttp = inject(WorkplaceAddressHttpService);
  private oAuthStorage = inject(OAuthStorageService);
  private workplacePolicyService = inject(WorkplacePolicyService);
  public snackbars = inject(WorkplaceSnackbarsService);

  public list$() {
    return this.listSubject.asObservable().pipe(shareReplay(1));
  }

  public get$() {
    return this.getSubject.asObservable().pipe(shareReplay(1));
  }

  public resetWorkplacesCache() {
    return this.workplaces.empty();
  }

  public resetWorkplaceCache() {
    return this.workplace.empty();
  }

  public list(companyID: number, params?: Params): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.list$(companyID, params).subscribe({
      next: this.nextList,
      error: this.errorList,
    });
  }

  private nextList = (response: WorkplaceApiObject): void => {
    if (!response.result) {
      this.errors.apiInvalidResponse(response);
      this.listSubject.next(null);
      return;
    }
    this.workplaces.set(response.result);
    this.listSubject.next(this.workplaces.get());
    this.linksSubject.next(response.links);
  };

  private errorList = (error: HttpErrorResponse): void => {
    this.listSubject.next(null);
    this.errors.communication(error);
  };

  public create(companyID: number, payload: WorkplaceDTO): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http
      .create$(companyID, payload)
      .pipe(tap(() => this.refreshStrictPolicy(companyID)))
      .subscribe({
        next: this.nextCreate,
        error: this.errorCreate,
      });
  }

  private nextCreate = (response: WorkplaceApiObject): void => {
    if (!response.result[0]) {
      this.getSubject.next(null);
      this.errors.apiInvalidResponse(response);

      return;
    }

    this.workplace.set(response.result[0]);
    this.workplaces.add(this.workplace.get());
    this.getSubject.next(this.workplace.get());
    this.snackbars.successRegister();
  };

  private errorCreate = (error: HttpErrorResponse): void => {
    this.getSubject.next(null);
    if (this.errors.isControlledError(error, 'E0004')) {
      this.snackbars.failureInvalidTimezone();
    }
    this.errors.communication(error);
  };

  public get(companyID: number, workplaceID: number): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    if (this.workplace.canReturn(companyID, workplaceID)) {
      this.getSubject.next(this.workplace.get());
    } else if (this.workplace.canApi(companyID, workplaceID)) {
      this.workplace.startToFly();
      this.http
        .get$(companyID, workplaceID)
        .pipe(finalize(() => this.workplace.endFlying()))
        .subscribe({
          next: this.nextGet,
          error: this.errorGet,
        });
    }
  }

  private nextGet = (response: WorkplaceApiObject): void => {
    if (!response.result[0]) {
      this.getSubject.next(null);
      this.errors.apiInvalidResponse(response);

      return;
    }

    this.workplace.set(response.result[0]);
    this.getSubject.next(this.workplace.get());
  };

  private errorGet = (error: HttpErrorResponse): void => {
    this.getSubject.next(null);
    this.errors.communication(error);
  };

  public update(
    companyID: number,
    workplaceID: number,
    payload: WorkplaceDTO
  ): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http
      .update$(companyID, workplaceID, payload)
      .pipe(tap(() => this.refreshStrictPolicy(companyID)))
      .subscribe({
        next: this.nextUpdate,
        error: this.errorUpdate,
      });
  }

  private nextUpdate = (response: WorkplaceApiObject): void => {
    if (!response.result[0]) {
      this.messageSubject.next(null);
      this.getSubject.next(null);
      this.errors.apiInvalidResponse(response);

      return;
    }

    this.workplace.set(response.result[0]);
    this.workplaces.update(this.workplace.get());
    this.getSubject.next(this.workplace.get());
    this.messageSubject.next('Ok updated workplace');
    this.snackbars.successUpdated();
  };

  private errorUpdate = (error: HttpErrorResponse): void => {
    this.messageSubject.next(null);
    this.getSubject.next(null);
    this.errors.communication(error);
  };

  public updateAddress(
    companyID: number,
    workplaceID: number,
    payload: WorkplaceAddressDTO
  ): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.addressHttp.update$(companyID, workplaceID, payload).subscribe({
      next: this.nextUpdateAddress,
      error: this.errorUpdateAddress,
    });
  }

  private nextUpdateAddress = (response: WorkplaceApiObject): void => {
    if (!response.result[0]) {
      this.getSubject.next(null);
      this.errors.apiInvalidResponse(response);

      return;
    }

    this.workplace.set(response.result[0]);
    this.workplaces.update(this.workplace.get());
    this.getSubject.next(this.workplace.get());
    this.snackbars.successAddressUpdated();
  };

  private errorUpdateAddress = (error: HttpErrorResponse): void => {
    this.getSubject.next(null);
    this.errors.communication(error);
  };

  public delete(companyID: number, workplaceID: number): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http
      .delete$(companyID, workplaceID)
      .pipe(tap(() => this.refreshStrictPolicy(companyID)))
      .subscribe({
        next: this.nextDelete,
        error: this.errorDelete,
      });
  }

  private nextDelete = (): void => {
    this.workplaces.empty();
    this.workplace.empty();
    this.getSubject.next(this.workplace.get());
    this.messageSubject.next('OK deleted workplace');
  };

  private errorDelete = (error: HttpErrorResponse): void => {
    this.getSubject.next(null);
    this.messageSubject.next(null);
    this.errors.communication(error);
  };

  private refreshStrictPolicy = (companyID: number): void => {
    this.workplacePolicyService.refreshStrictPolicy(companyID, true);
  };
}
